import type { TenantFeature } from '../enums/tenant-feature.enum.js'

/**
 * What a company without a feature sees where the feature would be.
 *
 * - `hidden` — nothing: no tab, no link, no mention.
 * - `cta` — the entry points stay, and show a call to action for the feature in
 *   place of the feature itself.
 *
 * Either way the feature's endpoints refuse; this is only about what is drawn.
 * Every feature has to say which, because the two are opposite promises — one
 * keeps a customer from knowing the feature exists, the other sells it to them.
 */
export type FeatureOffMode = 'hidden' | 'cta'

export interface TenantFeatureInfo {
  /** What the super admin's switch is labeled. */
  label: string
  /** One line saying what switching it on gives the company. */
  description: string
  offMode: FeatureOffMode
}

/**
 * Every feature, described once. A `Record` over the enum, so adding a feature
 * to `TENANT_FEATURES` without describing it here fails to compile.
 */
export const TENANT_FEATURE_INFO: Record<TenantFeature, TenantFeatureInfo> = {
  marketing: {
    label: 'Marketing',
    description: 'Public pricing pages for each venue, edited from venue settings.',
    offMode: 'hidden',
  },
  gohighlevel: {
    label: 'GoHighLevel',
    description: 'Connect venues to GoHighLevel and link events to the leads they came from.',
    offMode: 'hidden',
  },
  ads: {
    label: 'Ads',
    description: 'Bring each venue’s Google Ads spend, clicks and conversions into one place.',
    offMode: 'hidden',
  },
  honeybook: {
    label: 'HoneyBook',
    description: 'Connect venues’ HoneyBook accounts: their pipeline counts here, and their booked projects become events.',
    offMode: 'hidden',
  },
}
